import { Box, Tooltip, Typography, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { titleize } from 'inflection';
import { useContext } from 'react';

import CurrentUserContext from '@/Contexts/CurrentUser';
import { type DeliveryAndCollectionItemState } from '@/graphql/generated/graphql';
import { toDecimalHours } from '@/utils/dateTimeHelpers';

interface DiaryEntryItemStatesRendererProps {
  positionedItemStates: Array<DeliveryAndCollectionItemState & { column: number; totalColumns: number }>;
  issuesCount: number;
  eventsCount: number;
  cellSize: number;
}

const DiaryEntryItemStatesRenderer: React.FC<DiaryEntryItemStatesRendererProps> = ({
  positionedItemStates,
  issuesCount,
  eventsCount,
  cellSize,
}) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const getItemStateToolTipTitle = (itemState: DeliveryAndCollectionItemState) => {
    return (
      <Grid container direction="column" spacing={1} width={300}>
        <Typography variant="subtitle1" fontWeight="bold">
          {itemState.supplier}
        </Typography>
        <Typography variant="body2">
          <strong>Current State:</strong> {titleize(itemState.currentState)}
        </Typography>
        <Typography variant="body2">
          <strong>Set At:</strong> {dayjs(itemState.currentStateSetAt).format(dateTimePreference.time)}
        </Typography>
        {itemState.notes && (
          <Typography variant="body2">
            <strong>Notes:</strong> {itemState.notes}
          </Typography>
        )}
      </Grid>
    );
  };

  return (
    <>
      {positionedItemStates.map((itemState, index) => {
        const stateHour = toDecimalHours(dayjs(itemState.currentStateSetAt));
        const stateWidth = 60;

        return (
          <Tooltip key={`item-state-${itemState.id}`} title={getItemStateToolTipTitle(itemState)} placement="top" arrow>
            <Box
              id={`diary-entry-item-state-${itemState.id}`}
              sx={{
                position: 'absolute',
                left: `${stateHour * cellSize}px`,
                top: `${(issuesCount + eventsCount + index) * 50}px`,
                width: `${stateWidth}px`,
                height: '40px',
                backgroundColor: 'rgba(76, 175, 80, 0.8)',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '8px',
                '&:hover': {
                  backgroundColor: 'rgba(76, 175, 80, 1)',
                  transform: 'scale(1.2)',
                  zIndex: 10,
                },
              }}
            >
              <Typography variant="body2" noWrap>
                {itemState.supplier}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </>
  );
};

export default DiaryEntryItemStatesRenderer;
